import chatTokens from '@amzn/meridian-tokens/base/icon/chat';
import Button from '@amzn/meridian/button';
import Icon from '@amzn/meridian/icon';
import Input from '@amzn/meridian/input';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { ARIA_CHAT_NAMESPACE, MAX_QUERY_LENGTH } from './ariaChatConstants';
import { ChatMessage } from './ariaChatTypes';
import AriaMessageContent from './AriaMessageContent';
import { useAriaChatEnabled } from './useAriaChatEnabled';
import { useChatStream } from './useChatStream';
import { useMediaQuery } from './useMediaQuery';

const DRAWER_WIDTH = 440;
// Below this the drawer takes the full width
const FULL_WIDTH_QUERY = `(max-width: ${DRAWER_WIDTH + 200}px)`;

interface AriaChatWidgetProps {
    children: React.ReactNode;
}

const AriaChatWidget: React.FC<AriaChatWidgetProps> = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [sessionId] = useState(() => uuidv4());
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const { t, i18n } = useTranslation(ARIA_CHAT_NAMESPACE);
    const { sendMessage, isStreaming } = useChatStream(sessionId, i18n.language);
    const isFullWidth = useMediaQuery(FULL_WIDTH_QUERY);
    const isEnabled = useAriaChatEnabled();
    const isTooLong = inputValue.trim().length > MAX_QUERY_LENGTH;

    useEffect(() => {
        if (isOpen && messages.length === 0) {
            setMessages([
                {
                    id: uuidv4(),
                    role: 'assistant',
                    content: t('welcome-message'),
                    timestamp: Date.now(),
                },
            ]);
        }
    }, [isOpen, messages.length, t]);

    useEffect(() => {
        if (messagesContainerRef.current) {
            messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        }
    }, [messages]);

    const handleSend = useCallback(() => {
        const query = inputValue.trim();
        // Over-length messages aren't sent; the input stays as typed so it can be shortened.
        if (!query || isStreaming || query.length > MAX_QUERY_LENGTH) return;

        const userMessage: ChatMessage = {
            id: uuidv4(),
            role: 'user',
            content: query,
            timestamp: Date.now(),
        };

        const assistantMessageId = uuidv4();
        const assistantMessage: ChatMessage = {
            id: assistantMessageId,
            role: 'assistant',
            content: '',
            timestamp: Date.now(),
        };

        setMessages((prev) => [...prev, userMessage, assistantMessage]);
        setInputValue('');

        sendMessage(
            query,
            (token) => {
                setMessages((prev) =>
                    prev.map((msg) => (msg.id === assistantMessageId ? { ...msg, content: msg.content + token } : msg))
                );
            },
            () => {},
            (error) => {
                setMessages((prev) =>
                    prev.map((msg) => (msg.id === assistantMessageId ? { ...msg, content: error } : msg))
                );
            }
        );
    }, [inputValue, isStreaming, sendMessage]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    if (!isEnabled) {
        return <>{children}</>;
    }

    return (
        // On all but full-width (phone-sized) screens the drawer is a column beside the page, so it never
        // covers page content. While it's open the page is never squeezed below its minimum width (which
        // would clip centered content); if it doesn't fit beside the drawer it scrolls sideways instead.
        // The drawer stays between the header and footer, and its panel sticks to the top of the viewport
        // while the page scrolls, at most one viewport tall.
        <div style={{ display: 'flex', alignItems: 'stretch', position: 'relative' }}>
            <div style={{ flex: 1, minWidth: 0, overflowX: isOpen && !isFullWidth ? 'auto' : undefined }}>
                <div style={{ minWidth: isOpen && !isFullWidth ? 'min-content' : undefined }}>{children}</div>
            </div>

            {/* The launcher sticks to the bottom of the viewport while the content extends below it, and
                otherwise rests at the end of the content, so it never sits over the footer. It's outside
                the content column so the column's horizontal scrolling doesn't affect it. */}
            {!isOpen && (
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                        pointerEvents: 'none',
                        zIndex: 1000,
                    }}
                >
                    <div style={{ position: 'sticky', bottom: 24, padding: '0 24px 24px 0' }}>
                        <button
                            type={'button'}
                            onClick={() => setIsOpen(true)}
                            aria-label={t('openChat-ariaLabel')}
                            style={{
                                pointerEvents: 'auto',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '10px 18px',
                                border: 'none',
                                borderRadius: 24,
                                backgroundColor: '#0073bb',
                                color: '#fff',
                                fontSize: 14,
                                fontWeight: 600,
                                cursor: 'pointer',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.25)',
                            }}
                        >
                            <Icon tokens={chatTokens} />
                            {t('openChat-buttonLabel')}
                        </button>
                    </div>
                </div>
            )}

            {isOpen && (
                <aside
                    aria-label={t('chatAssistant-ariaLabel')}
                    style={{
                        backgroundColor: '#fafafa',
                        borderLeft: '1px solid #e0e0e0',
                        ...(isFullWidth
                            ? { position: 'absolute', top: 0, right: 0, bottom: 0, left: 0, zIndex: 1001 }
                            : { width: DRAWER_WIDTH, flexShrink: 0 }),
                    }}
                >
                    <div
                        style={{
                            position: 'sticky',
                            top: 0,
                            height: '100%',
                            maxHeight: '100vh',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                    >
                        {/* Header */}
                        <div
                            style={{
                                backgroundColor: '#0073bb',
                                padding: '14px 16px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                            }}
                        >
                            <span style={{ color: '#fff', fontWeight: 600, fontSize: 15, letterSpacing: 0.2 }}>
                                {t('chatAssistant-title')}
                            </span>
                            <button
                                type={'button'}
                                onClick={() => setIsOpen(false)}
                                aria-label={t('closeChat-ariaLabel')}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#fff',
                                    cursor: 'pointer',
                                    fontSize: 18,
                                    padding: 4,
                                    lineHeight: 1,
                                }}
                            >
                                {'✕'}
                            </button>
                        </div>

                        {/* Messages */}
                        <div ref={messagesContainerRef} style={{ flex: 1, overflowY: 'auto', padding: '16px 12px' }}>
                            {messages.map((msg) => (
                                <div
                                    key={msg.id}
                                    style={{
                                        display: 'flex',
                                        justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
                                        marginBottom: 10,
                                    }}
                                >
                                    <div
                                        style={{
                                            maxWidth: '82%',
                                            padding: '10px 14px',
                                            borderRadius:
                                                msg.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                                            backgroundColor: msg.role === 'user' ? '#0073bb' : '#e8eaed',
                                            color: msg.role === 'user' ? '#fff' : '#1a1a1a',
                                            fontSize: 14,
                                            lineHeight: 1.5,
                                            // User text is shown as typed; assistant Markdown handles its own line breaks
                                            whiteSpace: msg.role === 'user' ? 'pre-wrap' : 'normal',
                                            overflowWrap: 'anywhere',
                                            boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                                        }}
                                    >
                                        {msg.role === 'assistant' && msg.content && (
                                            <AriaMessageContent content={msg.content} />
                                        )}
                                        {msg.role === 'user' && msg.content}
                                        {!msg.content && isStreaming && '…'}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Input */}
                        <div style={{ padding: '12px', borderTop: '1px solid #e0e0e0', backgroundColor: '#fff' }}>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                <div style={{ flex: 1 }}>
                                    <Input
                                        id={'aria-chat-input'}
                                        value={inputValue}
                                        onChange={setInputValue}
                                        onKeyDown={handleKeyDown}
                                        placeholder={t('question-placeholder')}
                                        disabled={isStreaming}
                                        aria-label={t('question-ariaLabel')}
                                    />
                                </div>
                                <Button
                                    type={'primary'}
                                    size={'small'}
                                    onClick={handleSend}
                                    disabled={isStreaming || !inputValue.trim() || isTooLong}
                                >
                                    {t('send-buttonLabel')}
                                </Button>
                            </div>
                            {isTooLong && (
                                <div role={'alert'} style={{ marginTop: 6, fontSize: 12, color: '#b3261e' }}>
                                    {t('tooLong-hint', { maxLength: MAX_QUERY_LENGTH })}
                                </div>
                            )}
                        </div>
                    </div>
                </aside>
            )}
        </div>
    );
};

export default AriaChatWidget;
